<script setup lang="ts">
import { computed, ref } from "vue";
import IncidentLevel from "@dashboard/base/IncidentLevel";
import {
  applyIncidentSelectionCommand,
  generateIncidentLabelId,
  type IncidentSelectionCommand,
} from "@composable/useIncidentSelection";
import type { IncidentAlertsMap } from "@entity/Incident";

defineOptions({
  inheritAttrs: false,
});

interface Props {
  level: string;
  service: string;
  region: string;
  alerts: IncidentAlertsMap;
}

const props = defineProps<Props>();

const selectionCommand = defineModel<IncidentSelectionCommand | null>("selectionCommand", {
  default: null,
});

const tooltipVisible = ref(false);
const currentAlertIndex = ref(0);
const sequence = ref(0);

const alertEntries = computed(() => Object.entries(props.alerts));
const classname = computed(() => IncidentLevel.classname(props.level));

function expand(): void {
  const id = getNextIncidentAnchor();
  if (!id) {
    return;
  }

  sequence.value += 1;
  const command: IncidentSelectionCommand = {
    id,
    seq: sequence.value,
  };
  selectionCommand.value = command;
  applyIncidentSelectionCommand(command);
  scrollToPosition(id);
}

function getNextIncidentAnchor(): string {
  if (alertEntries.value.length === 0) {
    return "";
  }

  currentAlertIndex.value %= alertEntries.value.length;
  const [incidentId] = alertEntries.value[currentAlertIndex.value];
  currentAlertIndex.value = (currentAlertIndex.value + 1) % alertEntries.value.length;

  return generateIncidentLabelId(props.service, props.region, incidentId);
}

function scrollToPosition(id: string): void {
  const labelElement = document.getElementById(id);
  if (!labelElement) {
    return;
  }

  const getDescriptionVisible = (): boolean => {
    const descriptionElement = labelElement.nextElementSibling as HTMLElement | null;
    return descriptionElement?.style.display !== "none";
  };

  if (getDescriptionVisible()) {
    window.scrollBy(0, labelElement.getBoundingClientRect().top - 130);
    return;
  }

  const observer = new MutationObserver(() => {
    if (getDescriptionVisible()) {
      window.scrollBy(0, labelElement.getBoundingClientRect().top - 130);
      observer.disconnect();
    }
  });

  observer.observe(labelElement, { attributes: true, childList: true });
}

function toggleTooltip(): void {
  tooltipVisible.value = !tooltipVisible.value && Object.keys(props.alerts).length > 0;
}
</script>

<template>
  <div
    class="have-info-wrapper left large CId-incident-icon IId-incident-icon"
    :class="classname"
    data-dtm-element="incident-icon"
    :data-dtm-name="`${service}-${region}`"
    @click.prevent="expand"
    @mouseenter="toggleTooltip"
    @mouseleave="toggleTooltip"
  >
    <div class="have-info-icon">
      <div v-if="tooltipVisible" class="have-info-content">
        <div class="info-icon__tooltip">
          <p v-for="[alertId, alert] in alertEntries" :key="alertId">
            <span />
            {{ alert.description }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
